import React, { useState, useEffect, useRef } from 'react';

interface Stop {
    PlaceName: string;
    OgtType: string;
    Ll: [number, number];
    Id: number;
}

interface CustomAutocompleteProps {
    value: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    onQueryChange: (value: string, type: "from" | "to") => void;
    options: Stop[];
    onSelect: (selectedValue: string) => void;
    type: "from" | "to";
    label: string;
    id: string;
    onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
    errorMessage: string;
}

const CustomAutocomplete: React.FC<CustomAutocompleteProps> = ({
    value,
    onChange,
    onQueryChange,
    options,
    onSelect,
    type,
    label,
    id,
    onKeyDown,
    errorMessage,
}) => {

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);
    const [isClicking, setIsClicking] = useState<boolean>(false);
    const [isSelectionMade, setIsSelectionMade] = useState<boolean>(false);
    const [resultLenght, setResultLenght] = useState("");
    const [highlightedStopName, setHighlightedStopName] = useState<string>('');


    useEffect(() => {
        if (value.trim().length > 0 && options.length > 0 && !isSelectionMade) {
            setIsOpen(true);
        } else {
            setIsOpen(false);
        }
    }, [value, options, isSelectionMade]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        onChange(e);  
        onQueryChange(val, type);  
        setIsSelectionMade(false);
        setHighlightedIndex(-1);
    };

    const handleSelect = (selectedValue: string) => {
        setResultLenght("");
        onSelect(selectedValue);
        onChange({ target: { value: selectedValue } } as React.ChangeEvent<HTMLInputElement>);
        setIsOpen(false);
        setIsSelectionMade(true);
    };

    const handleKeyDownInternal = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowDown') {
            setHighlightedIndex((prevIndex) => Math.min(options.length - 1, prevIndex + 1));
        } else if (e.key === 'ArrowUp') {
            setHighlightedIndex((prevIndex) => Math.max(0, prevIndex - 1));
        } else if (e.key === 'Enter') {
            if (isOpen && highlightedIndex >= 0) {
                e.preventDefault();
                handleSelect(options[highlightedIndex].PlaceName);
            }
        }


        if (onKeyDown && !isOpen) {
            onKeyDown(e);
        }
    };

    const handleBlur = () => {
        setTimeout(() => {
            if (!isClicking && !isSelectionMade) {
                setIsSelectionMade(true);
                setIsOpen(false);
                const selectedOption =
                    highlightedIndex >= 0 && highlightedIndex < options.length
                        ? options[highlightedIndex].PlaceName
                        : options.length > 0
                            ? options[0].PlaceName
                            : "";

                if (selectedOption) {
                    onSelect(selectedOption);
                    onChange({ target: { value: selectedOption } } as React.ChangeEvent<HTMLInputElement>);
                }
            }
        }, 300);
    };

    const handleMouseDown = () => {
        setIsClicking(true);
    };

    const handleMouseUp = () => {
        setIsClicking(false);
    };

    const getIcon = (stopType: string) => {
        switch (stopType) {
            case 'address':
                return 'ogt-pin-dot-32'
            case 'stop':
                return 'ogt-pin-stop-32'
            case 'poi':
                return 'ogt-pin-star-32'
            default:
                return '';
        }
    };

    useEffect(() => {
        document.addEventListener('mouseup', handleMouseUp);
        return () => document.removeEventListener('mouseup', handleMouseUp);
    }, []);

    // Update for SR
    useEffect(() => {
        if (options.length > 0 && isOpen) {
            setResultLenght("");
            setTimeout(() => {
                setResultLenght(`Det finns ${options.length} sökförslag. Använd pilarna för att navigera`);
            }, 100);
        } else {
            setResultLenght("");
        }
    }, [options, isOpen]);

    // Reset the highlighted index
    useEffect(() => {
        setHighlightedIndex(-1);
    }, [options]);

    // Update current stop for SR
    useEffect(() => {
        if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            setHighlightedStopName(options[highlightedIndex].PlaceName);
        } else {
            setHighlightedStopName('');
        }
    }, [highlightedIndex, options]);

    return (
        <div
            className="autocomplete-container"
            onMouseDown={handleMouseDown}
        >
            <label htmlFor={id}><span>{label}<span>(Obligatoriskt)</span></span></label>
            {errorMessage.length > 0 && (
                <span className="validation-error">{errorMessage}</span>
            )}
            <input
                id={id}
                ref={inputRef}
                type="text"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDownInternal}
                onBlur={handleBlur}
            />
            {isOpen && options.length > 0 && (

                <ul
                    ref={dropdownRef}

                >
                    {options.map((option, index) => (
                        <li
                            key={`${option.Id}${index}`}
                            onClick={() => handleSelect(option.PlaceName)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            className={index === highlightedIndex ? 'active' : ''}
                        >
                            <i className={`icon--placetype ${getIcon(option.OgtType)}`}></i>
                            {option.PlaceName}
                        </li>
                    ))}
                </ul>

            )}

            <div aria-live="polite" className="sr-only" role="status">
                {resultLenght}
            </div>

            <div aria-live="polite" className="sr-only" role="status">
                {highlightedStopName}
            </div>
        </div>


    );
};

export default CustomAutocomplete;
